import { useMemo } from 'react';
import { MapLayerSettings, MapLayerToggle } from '../../api/mapping/types';
import { renderLayerSet } from './layers';
import { BufferedFrameCache } from './caching';

function filterEnabledLayers(
  layers: MapLayerSettings[],
  toggles: MapLayerToggle[],
  activeToggleIds: Set<string>,
): MapLayerSettings[] {
  return layers.filter((layer) => {
    // First check if the layer itself is enabled
    if (!layer.enabled) {
      return false;
    }

    // Find all toggles that contain this layer
    const containingToggles = toggles.filter((toggle) => toggle.layers.on?.includes(layer.id));

    // If the layer is not in any toggle, include it
    if (containingToggles.length === 0) {
      return true;
    }

    // Include the layer if at least one containing toggle is active
    return containingToggles.some((toggle) => activeToggleIds.has(toggle.title));
  });
}

function renderLayers(
  layers: MapLayerSettings[],
  frameCache: BufferedFrameCache,
  frameIndex: number,
  isAnimationPlaying: boolean = false,
  isDragging: boolean = false,
  isLoading: boolean = false,
) {
  return renderLayerSet(layers, frameCache, frameIndex, isAnimationPlaying, isDragging, isLoading);
}

export interface LayerManagerProps {
  readonly layers: MapLayerSettings[];
  readonly toggles: MapLayerToggle[];
  readonly activeToggleIds: Set<string>;
  readonly frameCache: BufferedFrameCache | null;
  readonly displayFrame: number | null;
  readonly isAnimationPlaying?: boolean;
  readonly isDragging?: boolean;
  readonly isLoading?: boolean;
}

export function useLayerManager({
  layers,
  toggles,
  activeToggleIds,
  frameCache,
  displayFrame,
  isAnimationPlaying = false,
  isDragging = false,
  isLoading = false,
}: LayerManagerProps) {
  const renderedLayers = useMemo(() => {
    if (frameCache != null && displayFrame !== null && frameCache.frames[displayFrame]) {
      const filteredLayers = filterEnabledLayers(layers, toggles, activeToggleIds);
      return renderLayers(
        filteredLayers,
        frameCache,
        displayFrame,
        isAnimationPlaying,
        isDragging,
        isLoading,
      );
    }
    return [];
  }, [
    layers,
    toggles,
    activeToggleIds,
    frameCache,
    displayFrame,
    isAnimationPlaying,
    isDragging,
    isLoading,
  ]);

  return renderedLayers;
}
